import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import React, { FunctionComponent, Ref, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import emptyIllustration from '../../../../assets/database_empty.svg';
import noResultsIllustration from '../../../../assets/database_no_results.svg';
import { HALF_ROW_SELECT_BUTTON_WIDTH } from '../../../../constants';
import CompactEmptyState from '../../CompactEmptyState';
import { useScope } from '../../Scope';

const Tr = styled.tr<{ isInitialized: boolean }>`
  margin-right: 1px;
  border-right: 1px solid ${token('color.border')};
  transition: opacity 1s;
  opacity: ${(props) => (props.isInitialized ? '1' : '0')};
`;

const EmptyState: FunctionComponent = () => {
  const { formatMessage } = useIntl();

  return (
    <CompactEmptyState
      heading={formatMessage(messages['table-layout.empty-database-empty-state-heading'])}
      description={formatMessage(
        messages['table-layout.empty-database-empty-state-description']
      )}
      imageUrl={emptyIllustration}
    />
  );
};

const NoResultsState: FunctionComponent = () => {
  const { formatMessage } = useIntl();

  return (
    <CompactEmptyState
      heading={formatMessage(
        messages['table-layout.no-matching-entries-empty-state-heading']
      )}
      description={formatMessage(
        messages['table-layout.no-matching-entries-empty-state-description']
      )}
      imageUrl={noResultsIllustration}
    />
  );
};

export interface TableLayoutEmptyStateRowProps {
  contentId: string;
  canEditContent: boolean;
  allFieldsHidden?: boolean;
}

const TableLayoutEmptyStateRow = React.memo(
  React.forwardRef<HTMLElement, TableLayoutEmptyStateRowProps>(
    ({ contentId, allFieldsHidden, canEditContent }, ref) => {
      const [isInitialized, setIsInitialized] = useState(false);
      useEffect(() => {
        setIsInitialized(true);
      }, []);

      const scope = useScope();
      const hasEntries = defaultStoreUtils.useEqualSelector((state) => {
        const databaseDraft = selectDatabaseDraftByContentId(state, {
          contentId,
          scope,
          includeDeleted: true,
        });

        return databaseDraft.entries.length > 0;
      });

      return (
        <Tr
          ref={ref as Ref<HTMLTableRowElement>}
          isInitialized={isInitialized}
          style={{
            gridTemplateColumns: 'auto',
            marginLeft: `${canEditContent ? HALF_ROW_SELECT_BUTTON_WIDTH : 0}px`,
          }}
        >
          <td>
            {hasEntries ? (
              allFieldsHidden ? (
                <EmptyState />
              ) : (
                <NoResultsState />
              )
            ) : (
              <EmptyState />
            )}
          </td>
        </Tr>
      );
    }
  )
);

export default TableLayoutEmptyStateRow;
